import { useEffect, useRef, useState } from "react";
import type { ImapPreset } from "../../../shared/types";
import { ImapConnectDialog } from "../mail/ImapConnectDialog";

// OAuth providers open a redirect; IMAP presets open the credentials dialog.
const OAUTH = [
  { label: "Microsoft", loginUrl: "/auth/login", glyph: "M", color: "#0f6cbd" },
  { label: "Google", loginUrl: "/auth/google/login", glyph: "G", color: "#ea4335" },
];
const IMAP: { label: string; preset: ImapPreset; glyph: string; color: string }[] = [
  { label: "iCloud", preset: "icloud", glyph: "", color: "#0a84ff" },
  { label: "Yahoo", preset: "yahoo", glyph: "Y!", color: "#6001d2" },
  { label: "Other IMAP", preset: "generic", glyph: "@", color: "#5a6b7b" },
];

/** "+" trigger with a popover to pick which provider to add. */
export function AddAccountButton({ variant }: { variant: "tab" | "card" }) {
  const [open, setOpen] = useState(false);
  const [imapPreset, setImapPreset] = useState<ImapPreset | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const glyph = (text: string, color: string) => (
    <span
      className="grid size-5 place-items-center rounded text-[10px] font-bold text-white"
      style={{ backgroundColor: color }}
    >
      {text}
    </span>
  );

  const menu = (
    <div className="absolute z-20 mt-1 w-56 overflow-hidden rounded-lg border border-stroke bg-white py-1 shadow-lg">
      {OAUTH.map((p) => (
        <a
          key={p.label}
          href={p.loginUrl}
          className="flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-accent-subtle"
        >
          {glyph(p.glyph, p.color)}
          Add {p.label} account
        </a>
      ))}
      <div className="my-1 border-t border-stroke" />
      {IMAP.map((p) => (
        <button
          key={p.preset}
          onClick={() => {
            setOpen(false);
            setImapPreset(p.preset);
          }}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] hover:bg-accent-subtle"
        >
          {glyph(p.glyph, p.color)}
          Add {p.label} account
        </button>
      ))}
    </div>
  );

  const trigger =
    variant === "card" ? (
      <button
        onClick={() => setOpen((v) => !v)}
        className="grid min-h-[150px] w-full place-items-center rounded-lg border-2 border-dashed border-stroke text-ink-muted hover:border-accent hover:text-accent"
      >
        <div className="text-center">
          <div className="text-[28px] leading-none">+</div>
          <div className="mt-1 text-[13px] font-medium">Add account</div>
        </div>
      </button>
    ) : (
      <button
        onClick={() => setOpen((v) => !v)}
        title="Add an account"
        className="px-3.5 text-[16px] text-ink-muted hover:text-accent"
      >
        +
      </button>
    );

  return (
    <>
      <div ref={ref} className="relative flex items-center">
        {trigger}
        {open && (
          <div className={variant === "tab" ? "absolute left-0 top-full" : ""}>{menu}</div>
        )}
      </div>
      {imapPreset && (
        <ImapConnectDialog
          preset={imapPreset}
          onConnected={(id) => {
            setImapPreset(null);
            const url = new URL(window.location.href);
            url.searchParams.set("account", String(id));
            window.location.href = url.toString();
          }}
          onClose={() => setImapPreset(null)}
        />
      )}
    </>
  );
}
